import { useEffect, useState } from 'react';

// Controles de camera (zoom + flash/torch) sobre o preview. Usa constraints da
// MediaStreamTrack (getCapabilities/applyConstraints). Cada controle so aparece
// se o aparelho suporta — na pratica Android Chrome expoe zoom/torch; iOS Safari
// normalmente nao, entao o componente some sozinho (nada quebra).
// Requer que o container-pai seja position:relative.

type FaixaZoom = { min: number; max: number; step: number };

export default function CameraControles({ stream }: { stream: MediaStream | null }) {
  const [zoom, setZoom] = useState<FaixaZoom | null>(null);
  const [zoomVal, setZoomVal] = useState(1);
  const [temTorch, setTemTorch] = useState(false);
  const [torchOn, setTorchOn] = useState(false);

  useEffect(() => {
    setZoom(null);
    setTemTorch(false);
    setTorchOn(false);

    const track = stream?.getVideoTracks?.()[0];
    // getCapabilities nao existe em todos os navegadores (ex.: iOS Safari).
    if (!track || typeof (track as { getCapabilities?: unknown }).getCapabilities !== 'function') return;

    const caps = track.getCapabilities() as unknown as { zoom?: { min?: number; max?: number; step?: number }; torch?: boolean };
    if (caps.zoom && typeof caps.zoom.max === 'number' && (caps.zoom.max > (caps.zoom.min ?? 1))) {
      const faixa: FaixaZoom = { min: caps.zoom.min ?? 1, max: caps.zoom.max, step: caps.zoom.step || 0.1 };
      setZoom(faixa);
      const settings = track.getSettings() as unknown as { zoom?: number };
      setZoomVal(typeof settings.zoom === 'number' ? settings.zoom : faixa.min);
    }
    if (caps.torch) setTemTorch(true);
  }, [stream]);

  function mudarZoom(v: number) {
    setZoomVal(v);
    const track = stream?.getVideoTracks?.()[0];
    track?.applyConstraints({ advanced: [{ zoom: v }] } as unknown as MediaTrackConstraints).catch(() => {});
  }

  function alternarTorch() {
    const track = stream?.getVideoTracks?.()[0];
    if (!track) return;
    const novo = !torchOn;
    track
      .applyConstraints({ advanced: [{ torch: novo }] } as unknown as MediaTrackConstraints)
      .then(() => setTorchOn(novo))
      .catch(() => {});
  }

  if (!zoom && !temTorch) return null;

  return (
    <div
      style={{
        position: 'absolute',
        left: 12,
        right: 12,
        bottom: 12,
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '8px 12px',
        borderRadius: 999,
        background: 'rgba(15, 23, 42, 0.55)',
        backdropFilter: 'blur(4px)',
      }}
    >
      {temTorch && (
        <button
          type="button"
          onClick={alternarTorch}
          title={torchOn ? 'Desligar flash' : 'Ligar flash'}
          style={{
            flexShrink: 0,
            width: 38,
            height: 38,
            borderRadius: '50%',
            border: 'none',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: torchOn ? '#facc15' : 'rgba(255,255,255,0.22)',
            color: torchOn ? '#1f2937' : '#fff',
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" fill={torchOn ? 'currentColor' : 'none'} />
          </svg>
        </button>
      )}

      {zoom && (
        <>
          <span style={{ color: '#fff', fontSize: 12, fontWeight: 800, flexShrink: 0 }}>{zoomVal.toFixed(1)}x</span>
          <input
            type="range"
            min={zoom.min}
            max={zoom.max}
            step={zoom.step}
            value={zoomVal}
            onChange={(e) => mudarZoom(Number(e.currentTarget.value))}
            style={{ flex: 1, accentColor: '#1e66f5' }}
          />
        </>
      )}
    </div>
  );
}
